import clsx from 'clsx';
import React, { FC, useRef, useState } from 'react';
import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { CustomPopover, UISystemIcon } from '@futrli/ui-components';
import { Forecast } from '@futrli-sdk/predict';

import { PREDICTION_ACTION } from '../../../../../core/constants';
import styles from '../../../../../core/common.scss';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';

interface Props {
  onSelect: (action: PREDICTION_ACTION) => void;
}

export const EditMenu: FC<Props> = ({ onSelect }) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { forecast } = usePredictionsInfoContext();

  const handleClick = (action: PREDICTION_ACTION) => (): void => {
    onSelect(action);
    toggleMenuOpen();
  };

  const toggleMenuOpen = () => {
    setMenuOpen(!menuOpen);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  if (forecast?.kind === 'budget' && (forecast as Forecast).is_frozen) {
    return null;
  }

  return (
    <div>
      <div
        data-testid="edit-menu-trigger"
        ref={anchorRef}
        onClick={handleOpen}
        style={{ marginRight: 8, cursor: 'pointer' }}
      >
        <Icon type="edit" fontSize="small" color="#ccd6db" />
      </div>

      <CustomPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        id="Period option"
        offset={{ x: 0, y: 0 }}
        style={{ padding: 0 }}
      >
        <div
          className={clsx(styles.uiMenuItemNewForecasts, styles.separator, styles.carbonMenuItemContainer)}
          onClick={handleClick(PREDICTION_ACTION.CONFIGURE)}
        >
          <div className={styles.menuItemIconContainer}>
            <Icon type="edit" fontSize="small" color="#ccd6db" />
          </div>
          <aside
            aria-label={intl.formatMessage({
              id: 'predict.edit_prediction',
              defaultMessage: 'Edit prediction'
            })}
          >
            <FormattedMessage id="predict.edit_prediction" defaultMessage="Edit prediction" />
          </aside>
        </div>
        <div
          className={clsx(styles.uiMenuItemNewForecasts, styles.separator, styles.carbonMenuItemContainer)}
          onClick={handleClick(PREDICTION_ACTION.QUICK_COPY)}
        >
          <div className={styles.menuItemIconContainer}>
            <Icon type="copy" fontSize="small" color="#ccd6db" />
          </div>
          <aside
            aria-label={intl.formatMessage({
              id: 'common.quick_copy',
              defaultMessage: 'Quick copy'
            })}
          >
            <FormattedMessage id="common.quick_copy" defaultMessage="Quick copy" />
          </aside>
        </div>
        <div
          className={clsx(styles.uiMenuItemNewForecasts, styles.separator, styles.carbonMenuItemContainer)}
          onClick={handleClick(PREDICTION_ACTION.RENAME)}
        >
          <div className={styles.menuItemIconContainer}>
            <UISystemIcon iconName="rename" />
          </div>
          <aside
            aria-label={intl.formatMessage({
              id: 'common.rename',
              defaultMessage: 'Rename'
            })}
          >
            <FormattedMessage id="common.rename" defaultMessage="Rename" />
          </aside>
        </div>
        <div
          className={clsx(styles.uiMenuItemNewForecasts, styles.separator, styles.carbonMenuItemContainer)}
          onClick={handleClick(PREDICTION_ACTION.DELETE)}
        >
          <div className={styles.menuItemIconContainer}>
            <Icon type="delete" fontSize="small" color="#ccd6db" />
          </div>
          <aside
            aria-label={intl.formatMessage({
              id: 'common.delete',
              defaultMessage: 'Delete'
            })}
          >
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </aside>
        </div>
      </CustomPopover>
    </div>
  );
};
